/**
 * CrewMobileCards Component
 * Mobile card view for crew list
 * Updated 2026-03-04: Added warning when toggling to On Duty with compliance issues
 * FT-16: Mobile responsive pass - larger text, 44px+ touch targets, optimized info density
 */

import React, { useState } from 'react';
import { Users, Phone, Mail, Ship, Anchor, AlertTriangle } from 'lucide-react';
import CrewQuickActions from './CrewQuickActions';
import { COMPLIANCE_COLORS, STATUS_COLORS } from '../utils/constants';
import { getCrewCompliance } from '../utils/crewHelpers';

/**
 * Get warning message for crew being toggled ON duty with compliance issues
 * Returns null if no issues, or a warning message string
 */
const getDutyToggleWarning = (member, familiarisationRecords = [], familiarisationValidityMonths = 12) => {
  const warnings = [];
  const today = new Date();

  // Check for expired certificates
  if (member.certificates && member.certificates.length > 0) {
    const expiredCerts = member.certificates.filter(cert => {
      if (!cert.expiry_date) return false;
      const expiryDate = new Date(cert.expiry_date);
      return expiryDate < today;
    }).map(cert => cert.name || cert.type || 'Certificate');

    if (expiredCerts.length > 0) {
      warnings.push(`Expired: ${expiredCerts.slice(0, 3).join(', ')}${expiredCerts.length > 3 ? '...' : ''}`);
    }
  }

  // Check medical expiry
  if (member.medical_expiry) {
    const medicalExpiry = new Date(member.medical_expiry);
    if (medicalExpiry < today) {
      warnings.push('Medical expired');
    }
  }

  // Check familiarisation status
  if (familiarisationRecords.length > 0) {
    const memberFamRecords = familiarisationRecords.filter(r => r.crew_id === member.id);
    if (memberFamRecords.length === 0) {
      warnings.push('No familiarisation');
    } else {
      // Check if most recent familiarisation is expired
      const latestFam = memberFamRecords.sort((a, b) =>
        new Date(b.date || b.completed_date) - new Date(a.date || a.completed_date)
      )[0];
      if (latestFam) {
        const famDate = new Date(latestFam.date || latestFam.completed_date);
        const expiryDate = new Date(famDate);
        expiryDate.setMonth(expiryDate.getMonth() + familiarisationValidityMonths);
        if (expiryDate < today) {
          warnings.push('Familiarisation expired');
        }
      }
    }
  }

  return warnings.length > 0 ? warnings.join(', ') : null;
};

const CrewMobileCards = ({
  filteredCrew,
  hoursData,
  vesselConfig,
  drillsData,
  familiarisationRecords = [],
  familiarisationValidityMonths = 12,
  onViewCrew,
  onEditCrew,
  onAddCrew,
  onQuickHOR,
  onQuickSignOn,
  onQuickSignOff,
  onToggleDutyStatus,  // NEW: Replaces onToggleOnboard
  isOnDuty,            // NEW: Helper function to check duty status
  searchTerm,
  isSyncing = false
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dutyWarning, setDutyWarning] = useState(null); // Track which member has active warning

  // Handle duty toggle with warning check
  const handleDutyToggle = (member) => {
    const wasOnDuty = isOnDuty && isOnDuty(member);

    // Execute the toggle
    if (onToggleDutyStatus) {
      onToggleDutyStatus(member);
    }

    // If toggling TO On Duty, check for warnings
    if (!wasOnDuty) {
      const warning = getDutyToggleWarning(member, familiarisationRecords, familiarisationValidityMonths);
      if (warning) {
        setDutyWarning({ memberId: member.id, message: warning });
        // Auto-clear warning after 5 seconds
        setTimeout(() => setDutyWarning(null), 5000);
      }
    }
  };

  const getComplianceColor = (type) => {
    return COMPLIANCE_COLORS[type] || COMPLIANCE_COLORS.default;
  };

  const getStatusColor = (status) => {
    return STATUS_COLORS[status] || STATUS_COLORS.default;
  };

  const handleToggleDropdown = (memberId, isOpen) => {
    setActiveDropdown(isOpen ? memberId : null);
  };

  if (filteredCrew.length === 0) {
    return (
      <div className="mt-8 grid grid-cols-1 gap-4 lg:hidden">
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">
            {isSyncing
              ? 'Syncing crew data...'
              : searchTerm
                ? `No crew members found matching "${searchTerm}"`
                : 'No crew members added yet'
            }
          </p>
          {!searchTerm && !isSyncing && (
            <button
              onClick={onAddCrew}
              className="mt-3 text-sm text-cyan-600 hover:text-cyan-500"
            >
              Add your first crew member
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 grid grid-cols-1 gap-4 lg:hidden" style={{ position: 'relative' }}>
      {filteredCrew.map((member) => {
        const compliance = getCrewCompliance(member, hoursData, vesselConfig, drillsData, familiarisationRecords, familiarisationValidityMonths);
        return (
          <div
            key={member.id}
            className="bg-white shadow rounded-xl p-4 cursor-pointer hover:shadow-md active:bg-gray-50 transition-all overflow-visible min-h-[80px]"
            onClick={() => onViewCrew(member)}
            style={{ position: 'relative' }}
          >
            {/* FT-16: Mobile-optimized card layout with proper touch targets */}
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  {/* FT-16: Larger name text for mobile readability - use member.name (matches desktop table) */}
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{member.name || member.full_name}</h3>
                  <div className="flex items-center gap-2">
                    {/* FT-16: Duty Status Pill - 44px+ touch target */}
                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDutyToggle(member);
                        }}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium ring-1 ring-inset transition-colors min-h-[44px] ${
                          isOnDuty && isOnDuty(member)
                            ? 'bg-cyan-50 text-cyan-700 ring-cyan-600/20 active:bg-cyan-100'
                            : 'bg-gray-50 text-gray-600 ring-gray-500/20 active:bg-gray-100'
                        }`}
                      >
                        {isOnDuty && isOnDuty(member) ? <Ship className="h-4 w-4" /> : <Anchor className="h-4 w-4" />}
                        {isOnDuty && isOnDuty(member) ? 'On Duty' : 'Off Duty'}
                      </button>
                      {/* Warning message for compliance issues */}
                      {dutyWarning && dutyWarning.memberId === member.id && (
                        <div className="flex items-center gap-1 text-xs text-amber-600">
                          <AlertTriangle className="h-3 w-3" />
                          <span className="truncate max-w-[120px]">{dutyWarning.message}</span>
                        </div>
                      )}
                    </div>
                    <CrewQuickActions
                      member={member}
                      isOpen={activeDropdown === member.id}
                      onToggle={(isOpen) => handleToggleDropdown(member.id, isOpen)}
                      onRecordHours={onQuickHOR}
                      onSignOn={onQuickSignOn}
                      onSignOff={onQuickSignOff}
                      onViewProfile={onViewCrew}
                      onEditDetails={onEditCrew}
                    />
                  </div>
                </div>
                {/* FT-16 FIX: Was showing member.id (UUID), now shows name correctly */}
                <p className="mt-1 text-sm text-gray-500">{member.position}</p>
                {/* Contact info */}
                <div className="mt-2 flex flex-wrap gap-3 text-sm">
                  {member.phone && (
                    <a 
                      href={`tel:${member.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 text-cyan-600 hover:text-cyan-900"
                    >
                      <Phone className="h-4 w-4" />
                      {member.phone}
                    </a>
                  )}
                  {member.email && (
                    <a 
                      href={`mailto:${member.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 text-cyan-600 hover:text-cyan-900"
                    >
                      <Mail className="h-4 w-4" />
                      <span className="truncate max-w-[150px]">{member.email}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
            {/* FT-265: Hide compliance/Edit bottom section when no issues — keeps card tidy on mobile/tablet */}
            {compliance.issues.length > 0 && (
              <div className="mt-3 border-t border-gray-200 pt-3">
                {/* FT-16: Larger compliance issue badges */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {compliance.issues.map((issue, idx) => (
                    <span
                      key={idx}
                      className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-sm font-medium ring-1 ring-inset ${getComplianceColor(issue.type)}`}
                    >
                      {issue.text}
                    </span>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  {/* FT-16: Edit button with 44px touch target */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditCrew(member);
                    }}
                    className="px-4 py-3 text-sm font-medium text-cyan-600 active:text-cyan-900 rounded-lg active:bg-cyan-50 transition-colors min-h-[44px]"
                  >
                    Edit
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CrewMobileCards;